import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { apiFetch } from './api';
import { hasPermissionSync, loadPermissions } from './auth/permissions';
import { SmartcardProvider, useSmartcard } from './contexts/SmartcardContext';
import { SmartcardWidget, SmartcardStatusBar } from './components/SmartcardWidget';
import PaymentWarningPopup from './components/PaymentWarningPopup';
import GlobalActivityTracker from './components/GlobalActivityTracker';
import Login from './pages/Login';
const EtbPage = lazy(() => import('./pages/EtbPage'));
const LagekartePage = lazy(() => import('./pages/LagekartePage'));
const IncidentReportsPage = lazy(() => import('./pages/incidents/IncidentReportsPage'));
const IncidentNotificationsPage = lazy(() => import('./pages/incidents/IncidentNotificationsPage'));
const Setup2FA = lazy(() => import('./pages/Setup2FA'));
const Pos = lazy(() => import('./pages/Pos'));
const Profile = lazy(() => import('./pages/ProfileExtended'));
import Logout from './pages/Logout';
const OverviewDashboard = lazy(() => import('./pages/OverviewDashboard'));
const POSDashboard = lazy(() => import('./pages/POSDashboard'));
import Lager from './pages/Lager';
const Warenlager = lazy(() => import('./pages/Warenlager'));
const Storage = lazy(() => import('./pages/Storage'));
const StorageStations = lazy(() => import('./pages/StorageStations'));
const BalanceManagement = lazy(() => import('./pages/BalanceManagement'));
const Admin = lazy(() => import('./pages/Admin'));
const MailAccountsAdmin = lazy(() => import('./pages/admin/MailAccountsAdmin'));
const ModuleContentAdmin = lazy(() => import('./pages/ModuleContentAdmin'));
const ThemeAdmin = lazy(() => import('./pages/admin/ThemeAdmin'));
const OrgRolesAdmin = lazy(() => import('./pages/OrgRolesAdmin'));
const MessagesAdmin = lazy(() => import('./pages/MessagesAdmin'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const SystemNotifications = lazy(() => import('./pages/SystemNotifications'));
const SystemNotificationsDashboard = lazy(() => import('./pages/SystemNotificationsDashboard'));
const QualifikationenAdmin = lazy(() => import('./pages/admin/QualifikationenAdmin'));
const InvoicesAdmin = lazy(() => import('./pages/admin/InvoicesAdmin'));
const DunningAdmin = lazy(() => import('./pages/admin/DunningAdmin'));
const DiensttypenAdmin = lazy(() => import('./pages/DiensttypenAdmin'));
const TagesDashboard = lazy(() => import('./pages/dienstplan/TagesDashboard'));
const TagesDashboardAdmin = lazy(() => import('./pages/dienstplan/TagesDashboardAdmin'));
const PublicTagesDashboard = lazy(() => import('./pages/PublicTagesDashboard'));
const Dienstplan = lazy(() => import('./pages/DienstplanKalender'));
const DienstplanVerwaltung = lazy(() => import('./pages/DienstplanVerwaltung'));
const AbsenceTypesManagement = lazy(() => import('./pages/AbsenceTypesManagement'));
const Sonderdienste = lazy(() => import('./pages/Sonderdienste'));
const SonderdiensteDokumente = lazy(() => import('./pages/SonderdiensteDokumente'));
const HelferPublicPortal = lazy(() => import('./pages/dienstplan/HelferPublicPortal'));
const HelferDashboard = lazy(() => import('./pages/dienstplan/HelferDashboard'));
const HelferAdminVerwaltung = lazy(() => import('./pages/dienstplan/HelferAdminVerwaltung'));
const HelferConfirmEmail = lazy(() => import('./pages/dienstplan/HelferConfirmEmail'));
const HelferResetPassword = lazy(() => import('./pages/dienstplan/HelferResetPassword'));
const DienstplanerInbox = lazy(() => import('./pages/dienstplan/DienstplanerInbox'));
const OpenShifts = lazy(() => import('./pages/dienstplan/OpenShifts'));
const MyRequests = lazy(() => import('./pages/dienstplan/MyRequests'));
const MyTimeTracking = lazy(() => import('./pages/dienstplan/MyTimeTracking'));
const TicketSystem = lazy(() => import('./pages/TicketSystem'));
const TicketSystemSettings = lazy(() => import('./pages/TicketSystemSettings'));
const MyExpenseRequests = lazy(() => import('./pages/MyRequests'));
const ExpenseClaimsManagement = lazy(() => import('./pages/ExpenseClaimsManagement'));
const PoolwaescheAdmin = lazy(() => import('./pages/poolwaesche/PoolwaescheAdmin'));
const PoolwaescheConfigure = lazy(() => import('./pages/poolwaesche/PoolwaescheConfigure'));
const PoolwaescheScanner = lazy(() => import('./pages/poolwaesche/PoolwaescheScanner'));
const PoolwaescheDashboard = lazy(() => import('./pages/poolwaesche/PoolwaescheDashboard'));
const PoolwaescheInventory = lazy(() => import('./pages/poolwaesche/PoolwaescheInventory'));
const StorageInventory = lazy(() => import('./pages/StorageInventory'));
const ShiftBillingConfig = lazy(() => import('./pages/ShiftBillingConfig'));
const ShiftBillingInvoices = lazy(() => import('./pages/ShiftBillingInvoices'));
const ShiftBillingInvoiceDetail = lazy(() => import('./pages/ShiftBillingInvoiceDetail'));
const LicenseControlScan = lazy(() => import('./pages/license-control/LicenseControlScan'));
const LicenseControlDashboard = lazy(() => import('./pages/license-control/LicenseControlDashboard'));
const LicenseControlInfo = lazy(() => import('./pages/license-control/LicenseControlInfo'));
const LicenseControlPopup = lazy(() => import('./pages/license-control/LicenseControlPopup'));
const LicenseControlPublicScan = lazy(() => import('./pages/license-control/LicenseControlPublicScan'));
const ExternalAccountCompleteView = lazy(() => import('./pages/onboarding/ExternalAccountCompleteView'));
const KioskTokenGenerator = lazy(() => import('./pages/license-control/KioskTokenGenerator'));
const CustomerDisplay = lazy(() => import('./pages/CustomerDisplay'));
const BonuspunkteProfile = lazy(() => import('./pages/bonuspunkte/BonuspunkteProfile'));
const BonuspunkteAdmin = lazy(() => import('./pages/bonuspunkte/BonuspunkteAdmin'));
const DocumentCategories = lazy(() => import('./pages/admin/DocumentCategories'));
const DocumentTemplates = lazy(() => import('./pages/admin/DocumentTemplates'));
const UserDocuments = lazy(() => import('./pages/admin/UserDocuments'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const ReportsArchive = lazy(() => import('./pages/admin/ReportsArchive'));
const ReportsConfig = lazy(() => import('./pages/admin/ReportsConfig'));
const FinalReports = lazy(() => import('./pages/admin/FinalReports'));
const OWIDatabase = lazy(() => import('./pages/admin/OWIDatabase'));
const TseConfiguration = lazy(() => import('./pages/TseConfiguration'));

// Wrapper für UserDocuments um userId aus Route zu extrahieren
function UserDocumentsWrapper() {
  const params = new URLSearchParams(window.location.pathname);
  const userId = parseInt(window.location.pathname.split('/').pop() || '0');
  return <Suspense fallback={<div>Lädt...</div>}><UserDocuments userId={userId} /></Suspense>;
}
const CashboxOverview = lazy(() => import('./pages/Vereinskasse/CashboxOverview'));
const CashboxDashboard = lazy(() => import('./pages/Vereinskasse/CashboxDashboard'));
const RecurringEntriesWrapper = lazy(() => import('./pages/Vereinskasse/RecurringEntriesWrapper'));
const Statistics = lazy(() => import('./pages/Statistics'));
const StatistikVerwaltung = lazy(() => import('./pages/statistics/StatistikVerwaltung'));
const DmsPortal = lazy(() => import('./pages/dms/DmsPortal'));
const DmsAdmin = lazy(() => import('./pages/dms/DmsAdmin'));
const FzgMatCheckDashboard = lazy(() => import('./pages/fzg-check/FzgMatCheckDashboard'));
const FzgMatCheckVerwaltung = lazy(() => import('./pages/fzg-check/FzgMatCheckVerwaltung'));
const FahrzeugverwaltungDashboard = lazy(() => import('./pages/FahrzeugverwaltungDashboard'));
const FahrzeugverwaltungVerwaltung = lazy(() => import('./pages/FahrzeugverwaltungVerwaltung'));
const FahrzeugtypenVerwaltung = lazy(() => import('./pages/FahrzeugtypenVerwaltung'));
const PersonalfunktionenVerwaltung = lazy(() => import('./pages/PersonalfunktionenVerwaltung'));
const TrustNetworkManagement = lazy(() => import('./pages/TrustNetworkManagement'));
const IncomingSharedShifts = lazy(() => import('./pages/IncomingSharedShifts'));
const Shop = lazy(() => import('./pages/Shop'));
const ShopVendor = lazy(() => import('./pages/ShopVendor'));
const ShopVendorConfig = lazy(() => import('./pages/ShopVendorConfig'));
const CrmDashboard = lazy(() => import('./pages/crm/CrmDashboard'));
const CrmCompanies = lazy(() => import('./pages/crm/CrmCompanies'));
const CrmCompanyForm = lazy(() => import('./pages/crm/CrmCompanyForm'));
const CrmCompanyDetail = lazy(() => import('./pages/crm/CrmCompanyDetail'));
const CrmQuotes = lazy(() => import('./pages/crm/CrmQuotes'));
const CrmQuoteForm = lazy(() => import('./pages/crm/CrmQuoteForm'));
const CrmQuoteDetail = lazy(() => import('./pages/crm/CrmQuoteDetail'));
const CrmQuoteEdit = lazy(() => import('./pages/crm/CrmQuoteEdit'));
const CrmContacts = lazy(() => import('./pages/crm/CrmContacts'));
const CrmContactForm = lazy(() => import('./pages/crm/CrmContactForm'));
const CrmContracts = lazy(() => import('./pages/crm/CrmContracts'));
const CrmContractForm = lazy(() => import('./pages/crm/CrmContractForm'));
const Chat = lazy(() => import('./pages/Chat'));
const CrmTasks = lazy(() => import('./pages/crm/CrmTasks'));
const CrmCommunications = lazy(() => import('./pages/crm/CrmCommunications'));
const CrmProjects = lazy(() => import('./pages/crm/CrmProjects'));
const CrmProjectForm = lazy(() => import('./pages/crm/CrmProjectForm'));
const CrmProjectDetail = lazy(() => import('./pages/crm/CrmProjectDetail'));
const CoursesEventsDashboard = lazy(() => import('./pages/courses-events/CoursesEventsDashboard'));
const CourseEventForm = lazy(() => import('./pages/courses-events/CourseEventForm'));
const CourseEventPlanning = lazy(() => import('./pages/courses-events/CourseEventPlanning'));
const CourseAdministration = lazy(() => import('./pages/courses-events/CourseAdministration'));
const CoursesPublicList = lazy(() => import('./pages/courses-events/CoursesPublicList'));
const CoursePublicBooking = lazy(() => import('./pages/courses-events/CoursePublicBooking'));
const CertificateTemplateList = lazy(() => import('./pages/courses-events/CertificateTemplateList'));
const CertificateTemplateEditor = lazy(() => import('./pages/courses-events/CertificateTemplateEditor'));
const OrgapilotCrmDashboard = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmDashboard'));
const WeaponsInventoryDashboard = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryDashboard'));
const OnboardingAdminDashboard = lazy(() => import('./pages/onboarding/OnboardingAdminDashboard'));
const AccountOrdersView = lazy(() => import('./pages/onboarding/AccountOrdersView'));
const EmployeePortalView = lazy(() => import('./pages/onboarding/EmployeePortalView'));
const MentorPortalView = lazy(() => import('./pages/onboarding/MentorPortalView'));

const WeaponsInventoryItems = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryItems'));
const WeaponsInventoryItemForm = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryItemForm'));
const WeaponsInventoryItemDetail = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryItemDetail'));
const WeaponsInventoryLocations = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryLocations'));
const WeaponsInventoryParties = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryParties'));
const WeaponsInventoryTransactions = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryTransactions'));
const WeaponsInventoryAudit = lazy(() => import('./pages/weapons-inventory/WeaponsInventoryAudit'));
const OrgapilotCrmCustomers = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmCustomers'));
const OrgapilotCrmAgreements = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmAgreements'));
const OrgapilotCrmOffers = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmOffers'));
const OrgapilotCrmOfferTemplates = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmOfferTemplates'));
const OrgapilotCrmOfferTemplateEditor = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmOfferTemplateEditor'));
const OrgapilotCrmDocuments = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmDocuments'));
const OrgapilotCrmNewsletters = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmNewsletters'));
const OrgapilotCrmBilling = lazy(() => import('./pages/orgapilot-crm/OrgapilotCrmBilling'));
const SystemImport = lazy(() => import('./pages/system-import/SystemImport'));
const Wiki = lazy(() => import('./pages/Wiki'));
const Changelog = lazy(() => import('./pages/Changelog'));
const SocialMediaDashboard = lazy(() => import('./pages/social/SocialMediaDashboard'));
const HotelDashboard = lazy(() => import('./pages/hotel/HotelDashboard'));
import { isAuthenticated } from './auth/auth';
import Layout from './components/Layout';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { ToastProvider } from './components/Toast';
import { ChatProvider, isExternalPublicRoute } from './contexts/ChatContext';


function PrivateRoute({ children }: { children: JSX.Element }) {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
}

const PORTAL_SEGMENT_TITLES: Record<string, string> = {
  admin: 'Administration',
  balance: 'Guthaben',
  bonuspunkte: 'Bonuspunkte',
  'customer-display': 'Kundendisplay',
  crm: 'CRM',
  'courses-events': 'Kurse / Events',
  dienstplan: 'Dienstplan',
  'license-control': 'Fuehrerscheinkontrolle',
  'license-scan': 'Smart-Kiosk',
  login: 'Login',
  logout: 'Logout',
  messages: 'Nachrichten',
  'pos-dashboard': 'POS Dashboard',
  pos: 'POS',
  profile: 'Profil',
  reports: 'Berichte',
  shop: 'Shop',
  'dms': 'Dokumente & Richtlinien (DMS)',
  'dms-verwaltung': 'DMS Verwaltung & Nachweise',
  'fzg-mat-check': 'FZG & MAT Check',
};

function toReadableTitle(segment: string) {
  if (!segment) return 'Dashboard';
  const mapped = PORTAL_SEGMENT_TITLES[segment];
  if (mapped) return mapped;
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function RequirePermission({ code, redirectTo, children }: { code: string; redirectTo: string; children: React.ReactNode }) {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>(
    hasPermissionSync(code) ? 'allowed' : 'loading',
  );
  useEffect(() => {
    if (state !== 'loading') return;
    loadPermissions().then(() => {
      setState(hasPermissionSync(code) ? 'allowed' : 'denied');
    }).catch(() => setState('denied'));
  }, [code, state]);
  if (state === 'loading') return <div className="p-6">Berechtigungen werden geprüft …</div>;
  if (state === 'denied') return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

function RequireSuper({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading');
  useEffect(() => {
    const org = localStorage.getItem('organization');
    const roles = JSON.parse(localStorage.getItem('roles') || '[]');
    const isSuper = org === 'SUPER' || roles.includes('SuperAdmin');
    setState(isSuper ? 'allowed' : 'denied');
  }, []);
  if (state === 'loading') return <div className="p-6">Berechtigungen werden geprüft …</div>;
  if (state === 'denied') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function getPortalTitle(pathname: string) {
  if (pathname === '/') {
    return 'OrgaPilot | Dashboard';
  }

  const segments = pathname.split('/').filter(Boolean);
  const relevant = segments[0] || 'dashboard';
  return `OrgaPilot | ${toReadableTitle(relevant)}`;
}

function PortalDocumentTitle() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/courses-events/public')) {
      return;
    }
    document.title = getPortalTitle(location.pathname);
  }, [location.pathname]);

  return null;
}

// ============================================
// SMARTCARD PAGE
// ============================================
const ProjectDashboard = lazy(() => import('./pages/project/ProjectDashboard'));
const ProjectNew = lazy(() => import('./pages/project/ProjectNew'));
const ProjectDetail = lazy(() => import('./pages/project/ProjectDetail'));

function SmartcardPage() {
  const { lastScannedCode } = useSmartcard();
  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-1">💳 Smartcard-Lesegerät</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Verbinde ein Kartenlesegerät (NFC/RFID) mit diesem Computer, um Ausweise und Karten zu scannen.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-4">
        <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3">Verbindungsstatus</h2>
        <SmartcardWidget showError={true} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-4">
        <h2 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-3">Letzter Scan</h2>
        {lastScannedCode ? (
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl">💳</span>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Gescannter Code:</div>
              <div className="font-mono text-lg font-bold text-gray-800 dark:text-white">{lastScannedCode}</div>
            </div>
          </div>
        ) : (
          <div className="text-gray-400 dark:text-gray-500 text-sm italic">
            Noch kein Scan – halte eine Karte an das Lesegerät.
          </div>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5 text-sm text-blue-800 dark:text-blue-200">
        <p className="font-semibold mb-2">💡 Browser-Hinweise</p>
        <ul className="space-y-1 list-disc list-inside">
          <li><strong>Chrome / Edge</strong>: Unterstützt WebHID (empfohlen für NFC-Lesegeräte)</li>
          <li><strong>Firefox / Safari</strong>: Eingeschränkte Unterstützung – verwende Chrome für beste Kompatibilität</li>
          <li>Das Lesegerät muss als HID-Gerät (Tastatur-Emulation) oder WebUSB-Gerät angeschlossen sein</li>
        </ul>
      </div>
    </div>
  );
}

export default function App() {
  const [hasDashboardModule, setHasDashboardModule] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState<any>(null);
  const [checkingLicense, setCheckingLicense] = useState(true);
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);

  useEffect(() => {
    // Check organization modules to decide whether Dashboard is enabled for this org
    const token = localStorage.getItem('token');
    if (!token) {
      setHasDashboardModule(true);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch('/admin/organization-modules');
        const org = localStorage.getItem('organization');
        const my = res.find((x: any) => x.organization_short_code === org && x.module_code === 'dashboard');
        setHasDashboardModule(!!(my && my.enabled));
      } catch (e) {
        setHasDashboardModule(true);
      }
    })()
  }, []);

  // Check license control status
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || isExternalPublicRoute()) {
      setCheckingLicense(false);
      return;
    }
    
    (async () => {
      try {
        const status = await apiFetch('/license-control/status');
        setLicenseStatus(status);
      } catch (e) {
        // If endpoint doesn't exist or user doesn't have license control, ignore
        setLicenseStatus(null);
      } finally {
        setCheckingLicense(false);
      }
    })();
  }, []);

  // Check payment status on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || isExternalPublicRoute()) return;
    
    (async () => {
      try {
        const status = await apiFetch('/admin/invoices/payment-status/check');
        if (status.locked || status.warning) {
          setShowPaymentWarning(true);
        }
      } catch (e) {
        // Ignore if endpoint doesn't exist
      }
    })();
  }, []);

  // Don't render anything until license check is done
  if (checkingLicense) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin text-6xl">⏳</div>
      </div>
    );
  }

  return (
    <DarkModeProvider>
      <ToastProvider>
        <SmartcardProvider>
          <ChatProvider>
            {!isExternalPublicRoute() && <SmartcardStatusBar />}

          {/* Show license control popup if user is blocked */}
          {!isExternalPublicRoute() && licenseStatus?.blocked && (
            <Suspense fallback={null}>
              <LicenseControlPopup />
            </Suspense>
          )}
          {/* Show payment warning popup */}
          {!isExternalPublicRoute() && showPaymentWarning && (
            <PaymentWarningPopup onClose={() => setShowPaymentWarning(false)} />
          )}
          <BrowserRouter>
            <PortalDocumentTitle />
            <GlobalActivityTracker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/setup-2fa" element={<Suspense fallback={<div className="p-6 text-center">Lade 2FA...</div>}><Setup2FA /></Suspense>} />
          <Route path="/logout" element={<Logout />} />

          {/* Öffentlicher Führerschein-Scan (ohne Login) */}
          <Route
            path="/license-scan"
            element={
              <Suspense fallback={<div className="p-6">Lade Scanner...</div>}>
                <LicenseControlPublicScan />
              </Suspense>
            }
          />

          {/* Öffentliche Account-Fertigmeldung per Ablauf-Link (ohne Login) */}
          <Route
            path="/onboarding/portal/account/:token"
            element={
              <Suspense fallback={<div className="p-6">Lade Portal...</div>}>
                <ExternalAccountCompleteView />
              </Suspense>
            }
          />

          {/* Öffentliches Kunden-Display (ohne Login) */}
          <Route
            path="/customer-display"
            element={
              <Suspense fallback={<div className="p-6">Lade Kunden-Display...</div>}>
                <CustomerDisplay />
              </Suspense>
            }
          />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dashboard...</div>}>
                    <OverviewDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/wiki"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Wiki...</div>}>
                    <Wiki />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/changelog"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Change-Log...</div>}>
                    <Changelog />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/chat"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Chat...</div>}>
                    <Chat />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/pos-dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade POS Dashboard...</div>}>
                    <POSDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/pos"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade POS...</div>}>
                    <Pos />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Profil...</div>}>
                    <Profile />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/lager"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Warenlager...</div>}>
                    <Warenlager />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/storage"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Lagerverwaltung...</div>}>
                    <Storage />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/balance"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Guthaben-Verwaltung...</div>}>
                    <BalanceManagement />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Admin...</div>}>
                    <Admin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/mail-accounts"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Mail-Accounts...</div>}>
                    <MailAccountsAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/module-content"
            element={
              <PrivateRoute>
                <RequireSuper>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Modul-Inhalte...</div>}>
                      <ModuleContentAdmin />
                    </Suspense>
                  </Layout>
                </RequireSuper>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/theme"
            element={
              <PrivateRoute>
                <RequireSuper>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Theme &amp; Effekte...</div>}>
                      <ThemeAdmin />
                    </Suspense>
                  </Layout>
                </RequireSuper>
              </PrivateRoute>
            }
          />

          <Route
            path="/hotel"
            element={
              <PrivateRoute>
                <RequireSuper>
                  <Suspense fallback={<div className="p-6">Lade OrgaPilot.HOTEL...</div>}>
                    <HotelDashboard />
                  </Suspense>
                </RequireSuper>
              </PrivateRoute>
            }
          />

          <Route
            path="/media"
            element={
              <PrivateRoute>
                <RequireSuper>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade OrgaPilot.MEDIA...</div>}>
                      <SocialMediaDashboard />
                    </Suspense>
                  </Layout>
                </RequireSuper>
              </PrivateRoute>
            }
          />

          <Route
            path="/verwaltung/rollen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Org-Rollen...</div>}>
                    <OrgRolesAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Nachrichten...</div>}>
                    <MessagesAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/activity-log"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Aktivitätsprotokoll...</div>}>
                    <ActivityLog />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/meldewesen/notifications"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Benachrichtigungen...</div>}>
                    <SystemNotifications />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/system-notifications"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade System-Benachrichtigungen...</div>}>
                    <SystemNotificationsDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/qualifikationen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Qualifikationen...</div>}>
                    <QualifikationenAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/diensttypen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Diensttypen...</div>}>
                    <DiensttypenAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/invoices"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Rechnungen...</div>}>
                    <InvoicesAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/dunning"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Mahnwesen...</div>}>
                    <DunningAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/document-categories"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dokumentkategorien...</div>}>
                    <DocumentCategories />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/document-templates"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dokumentvorlagen...</div>}>
                    <DocumentTemplates />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/user-documents/:userId"
            element={
              <PrivateRoute>
                <Layout>
                  <UserDocumentsWrapper />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/tse-configuration"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade TSE-Konfiguration...</div>}>
                    <TseConfiguration />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/tages-dashboard/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Verwaltung...</div>}>
                    <TagesDashboardAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/dienstplan/tages-dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Tages-Dashboard...</div>}>
                    <TagesDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/public/tagesplan/:token"
            element={
              <Suspense fallback={<div className="p-6">Lade öffentlichen Tagesplan...</div>}>
                <PublicTagesDashboard />
              </Suspense>
            }
          />
          <Route
            path="/dienstplan"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienstplan...</div>}>
                    <Dienstplan />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/dienstplankalender" element={<Navigate to="/dienstplan" replace />} />
          <Route path="/fahrzeugverwaltung/dashboard" element={<Navigate to="/fahrzeugverwaltung" replace />} />

          <Route
            path="/dienstplan-verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienstplan-Verwaltung...</div>}>
                    <DienstplanVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienstplan-Verwaltung...</div>}>
                    <DienstplanVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/zeiterfassung-verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienstplan-Verwaltung...</div>}>
                    <DienstplanVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/zeiterfassung/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienstplan-Verwaltung...</div>}>
                    <DienstplanVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/absence-types"
            element={<Navigate to="/dienstplan-verwaltung" replace />}
          />

          <Route
            path="/sonderdienste"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dienste & Veranstaltungen...</div>}>
                    <Sonderdienste />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/sonderdienste/dokumente"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade mitgeltende Dokumente...</div>}>
                    <SonderdiensteDokumente />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/trust-network"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Vertrauensstellungen...</div>}>
                    <TrustNetworkManagement />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shop"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Shop...</div>}>
                    <Shop />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shop/vendor"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Bestellverwaltung...</div>}>
                    <ShopVendor />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shop/vendor/config"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Konfiguration...</div>}>
                    <ShopVendorConfig />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/inbox"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Inbox...</div>}>
                    <DienstplanerInbox />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/open"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade offene Dienste...</div>}>
                    <OpenShifts />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/my-requests"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Anträge...</div>}>
                    <MyRequests />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dienstplan/zeiterfassung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Zeiterfassung...</div>}>
                    <MyTimeTracking />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/zeiterfassung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Zeiterfassung...</div>}>
                    <MyTimeTracking />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/lagekarte"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Lagekarte...</div>}>
                    <LagekartePage />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/etb"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Einsatztagebuch...</div>}>
                    <EtbPage />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/ereignismeldungen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Personal & Ereignismeldungen...</div>}>
                    <IncidentReportsPage />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/ereignismeldungen/notifications"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Benachrichtigungen...</div>}>
                    <IncidentNotificationsPage />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route path="/incident-reports" element={<Navigate to="/ereignismeldungen" replace />} />
          <Route path="/incident-reports/*" element={<Navigate to="/ereignismeldungen" replace />} />

          <Route
            path="/meldewesen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Ticket-System...</div>}>
                    <TicketSystem />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/meldewesen/settings"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Einstellungen...</div>}>
                    <TicketSystemSettings />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/my-expense-requests"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Auslagen...</div>}>
                    <MyExpenseRequests />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/expense-claims"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Auslagen-Verwaltung...</div>}>
                    <ExpenseClaimsManagement />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/bonuspunkte"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Bonuspunkte...</div>}>
                    <BonuspunkteProfile />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/bonuspunkte/admin"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Bonuspunkte-Verwaltung...</div>}>
                    <BonuspunkteAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/bonuspunkte"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Bonuspunkte-Verwaltung...</div>}>
                    <BonuspunkteAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Poolwäsche...</div>}>
                    <PoolwaescheAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dashboard...</div>}>
                    <PoolwaescheDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche/inventory"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Poolwäsche-Inventur...</div>}>
                    <PoolwaescheInventory />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche/inventory/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Inventur...</div>}>
                    <PoolwaescheInventory />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/storage/inventory"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Lager-Inventur...</div>}>
                    <StorageInventory />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/storage/stations"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Standort-Lager...</div>}>
                    <StorageStations />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/storage/inventory/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Lager-Inventur...</div>}>
                    <StorageInventory />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shift-billing/config"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Konfiguration...</div>}>
                    <ShiftBillingConfig />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shift-billing/invoices"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Rechnungen...</div>}>
                    <ShiftBillingInvoices />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/shift-billing/invoices/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Rechnung...</div>}>
                    <ShiftBillingInvoiceDetail />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche/configure"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Konfiguration...</div>}>
                    <PoolwaescheConfigure />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/poolwaesche/scanner"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Scanner...</div>}>
                    <PoolwaescheScanner />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/vereinskasse"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Vereinskasse...</div>}>
                    <CashboxOverview />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/vereinskasse/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Kasse...</div>}>
                    <CashboxDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/cashbox/:id/recurring"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade wiederkehrende Buchungen...</div>}>
                    <RecurringEntriesWrapper />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/license-control/scan"
            element={
              <PrivateRoute>
                <Suspense fallback={<div className="p-6">Lade Führerscheinkontrolle...</div>}>
                  <LicenseControlScan />
                </Suspense>
              </PrivateRoute>
            }
          />

          <Route
            path="/license-control/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dashboard...</div>}>
                    <LicenseControlDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/license-control/info"
            element={
              <PrivateRoute>
                <Suspense fallback={<div className="p-6">Lade Info...</div>}>
                  <LicenseControlInfo />
                </Suspense>
              </PrivateRoute>
            }
          />

          <Route
            path="/license-control/kiosk-token"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Token-Generator...</div>}>
                    <KioskTokenGenerator />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/statistik"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Statistik...</div>}>
                    <Statistics />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/statistik/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Statistik-Verwaltung...</div>}>
                    <StatistikVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dms"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Dokumenten-Bibliothek...</div>}>
                    <DmsPortal />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dms/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade DMS Verwaltung & Nachweise...</div>}>
                    <DmsAdmin />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fzg-mat-check"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade FZG & MAT Check...</div>}>
                    <FzgMatCheckDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fzg-mat-check/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade FZG & MAT Check Verwaltung...</div>}>
                    <FzgMatCheckVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fahrzeugverwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Fahrzeugverwaltung...</div>}>
                    <FahrzeugverwaltungDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fahrzeugverwaltung/verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Fahrzeugverwaltung...</div>}>
                    <FahrzeugverwaltungVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fahrzeugverwaltung/typen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Fahrzeugtypen...</div>}>
                    <FahrzeugtypenVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/fahrzeugverwaltung/personal-funktionen"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Personalfunktionen...</div>}>
                    <PersonalfunktionenVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/reports"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Berichte...</div>}>
                    <Reports />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/reports-archive"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Archiv...</div>}>
                    <ReportsArchive />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/owi-database"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade OWI-Datenbank...</div>}>
                    <OWIDatabase />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/reports-config"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Konfiguration...</div>}>
                    <ReportsConfig />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/final-reports"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Sammelberichte...</div>}>
                    <FinalReports />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/final-reports/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Sammelbericht...</div>}>
                    <FinalReports />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          {/* CRM Routes */}
          <Route
            path="/crm"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade CRM Dashboard...</div>}>
                    <CrmDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/companies/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmCompanyForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/companies/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmCompanyForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/companies/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Firma...</div>}>
                    <CrmCompanyDetail />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/companies"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Firmen...</div>}>
                    <CrmCompanies />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/quotes/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmQuoteForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/quotes/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmQuoteEdit />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/quotes/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Angebot...</div>}>
                    <CrmQuoteDetail />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/quotes"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Angebote...</div>}>
                    <CrmQuotes />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contacts/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmContactForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contacts/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmContactForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contacts"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Kontakte...</div>}>
                    <CrmContacts />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contracts/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmContractForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contracts/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmContractForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/contracts"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Verträge...</div>}>
                    <CrmContracts />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/tasks"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Aufgaben...</div>}>
                    <CrmTasks />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/communications"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Kommunikation...</div>}>
                    <CrmCommunications />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/projects/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmProjectForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/projects/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CrmProjectForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/crm/projects/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Projekt...</div>}>
                    <CrmProjectDetail />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/helfer-plattform"
            element={
              <div className="min-h-screen w-full bg-slate-950 text-white flex items-center justify-center p-6 text-center font-sans">
                <div className="max-w-md bg-slate-900 p-8 rounded-2xl border border-red-500/30 shadow-2xl space-y-4">
                  <h1 className="text-2xl font-bold text-red-500 font-display">Fehlender Portal-Link</h1>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Bitte nutzen Sie den vollständigen Link Ihrer Organisation (z.B. mit dem persönlichen Portal-Token am Ende des Links), um auf die Helfer-Plattform zuzugreifen.
                  </p>
                </div>
              </div>
            }
          />
          <Route
            path="/helfer-plattform/:token"
            element={
              <Suspense fallback={<div className="p-6">Lade Portal...</div>}>
                <HelferPublicPortal />
              </Suspense>
            }
          />
          <Route
            path="/helfer-plattform/:token/dashboard"
            element={
              <Suspense fallback={<div className="p-6">Lade Dashboard...</div>}>
                <HelferDashboard />
              </Suspense>
            }
          />
          <Route
            path="/helfer-plattform/:token/confirm-email"
            element={
              <Suspense fallback={<div className="p-6">Lade Bestätigung...</div>}>
                <HelferConfirmEmail />
              </Suspense>
            }
          />
          <Route
            path="/helfer-plattform/:token/reset-password"
            element={
              <Suspense fallback={<div className="p-6">Lade Passwort zurücksetzen...</div>}>
                <HelferResetPassword />
              </Suspense>
            }
          />
          <Route
            path="/dienstplan/helfer-verwaltung"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Helfer-Verwaltung...</div>}>
                    <HelferAdminVerwaltung />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses-events/public/course/:publicId"
            element={
              <Suspense fallback={<div className="p-6">Lade Kurs...</div>}>
                <CoursePublicBooking />
              </Suspense>
            }
          />
          <Route
            path="/courses-events/public/list/:token"
            element={
              <Suspense fallback={<div className="p-6">Lade Kurse...</div>}>
                <CoursesPublicList />
              </Suspense>
            }
          />
          <Route
            path="/courses-events/public/:org/:slug"
            element={
              <Suspense fallback={<div className="p-6">Lade Kurs...</div>}>
                <CoursePublicBooking />
              </Suspense>
            }
          />
          <Route
            path="/courses-events/public/:org"
            element={
              <Suspense fallback={<div className="p-6">Lade Kurse...</div>}>
                <CoursesPublicList />
              </Suspense>
            }
          />
          <Route
            path="/courses-events/new"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CourseEventForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses-events/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Formular...</div>}>
                    <CourseEventForm />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses-events/:id/planning"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Veranstaltungsplanung...</div>}>
                    <CourseEventPlanning />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses-events"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Kurse / Events...</div>}>
                    <CoursesEventsDashboard />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/courses-events/administration"
            element={
              <RequirePermission code="courses:admin" redirectTo="/courses-events">
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Kurs-Administration...</div>}>
                      <CourseAdministration />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              </RequirePermission>
            }
          />
          <Route
            path="/courses-events/certificate-templates/new"
            element={
              <RequirePermission code="courses:admin" redirectTo="/courses-events">
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Editor...</div>}>
                      <CertificateTemplateEditor />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              </RequirePermission>
            }
          />
          <Route
            path="/courses-events/certificate-templates/:id/edit"
            element={
              <RequirePermission code="courses:admin" redirectTo="/courses-events">
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Editor...</div>}>
                      <CertificateTemplateEditor />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              </RequirePermission>
            }
          />
          <Route
            path="/courses-events/certificate-templates"
            element={
              <RequirePermission code="courses:admin" redirectTo="/courses-events">
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="p-6">Lade Vorlagen...</div>}>
                      <CertificateTemplateList />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              </RequirePermission>
            }
          />
          <Route
            path="/crm/projects"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<div className="p-6">Lade Projekte...</div>}>
                    <CrmProjects />
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />

          {/* OrgaPilot CRM (nur Organisation SUPER) */}
          <Route path="/orgapilot-crm" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade OrgaPilot CRM...</div>}>
                <OrgapilotCrmDashboard />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/customers" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Kunden...</div>}>
                <OrgapilotCrmCustomers />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/agreements" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Abmachungen...</div>}>
                <OrgapilotCrmAgreements />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/offers" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Angebote...</div>}>
                <OrgapilotCrmOffers />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/offer-templates" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Vorlagen...</div>}>
                <OrgapilotCrmOfferTemplates />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/offer-templates/new" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Editor...</div>}>
                <OrgapilotCrmOfferTemplateEditor />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/offer-templates/:id/edit" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Editor...</div>}>
                <OrgapilotCrmOfferTemplateEditor />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/documents" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Dokumente...</div>}>
                <OrgapilotCrmDocuments />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/newsletters" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Newsletter...</div>}>
                <OrgapilotCrmNewsletters />
              </Suspense>
            </Layout></PrivateRoute>
          } />
          <Route path="/orgapilot-crm/billing" element={
            <PrivateRoute><Layout>
              <Suspense fallback={<div className="p-6">Lade Abrechnung...</div>}>
                <OrgapilotCrmBilling />
              </Suspense>
            </Layout></PrivateRoute>
          } />

          
          {/* OrgaPilot.ONBOARDING */}
          <Route path="/onboarding" element={<Navigate to="/onboarding/admin" replace />} />
          <Route path="/onboarding/admin" element={
            <RequireSuper>
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Onboarding Admin...</div>}>
                  <OnboardingAdminDashboard />
                </Suspense>
              </Layout></PrivateRoute>
            </RequireSuper>
          } />
          <Route path="/onboarding/accounts" element={
            <RequireSuper>
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Account-Aufträge...</div>}>
                  <AccountOrdersView />
                </Suspense>
              </Layout></PrivateRoute>
            </RequireSuper>
          } />
          <Route path="/onboarding/me" element={
            <RequireSuper>
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Mein Onboarding...</div>}>
                  <EmployeePortalView />
                </Suspense>
              </Layout></PrivateRoute>
            </RequireSuper>
          } />
          <Route path="/onboarding/mentor" element={
            <RequireSuper>
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Mentoren-Portal...</div>}>
                  <MentorPortalView />
                </Suspense>
              </Layout></PrivateRoute>
            </RequireSuper>
          } />

          {/* Waffen- & Einsatzmittelverwaltung */}
          <Route path="/weapons-inventory" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryDashboard /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/items" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryItems /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/items/new" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryItemForm /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/items/:id" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryItemDetail /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/items/:id/edit" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryItemForm /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/locations" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryLocations /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/parties" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryParties /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/transactions" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryTransactions /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/weapons-inventory/audit" element={
            <PrivateRoute><Layout><Suspense fallback={<div className="p-6">Lädt…</div>}><WeaponsInventoryAudit /></Suspense></Layout></PrivateRoute>
          } />
          <Route path="/system-import" element={
            <RequireSuper>
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Datenimport...</div>}>
                  <SystemImport />
                </Suspense>
              </Layout></PrivateRoute>
            </RequireSuper>
          } />

          {/* Smartcard-Lesegerät Seite */}
          <Route
            path="/smartcard"
            element={
              <PrivateRoute>
                <Layout>
                  <SmartcardPage />
                </Layout>
              </PrivateRoute>
            }
          />


          {/* OrgaPilot.PROJECT */}
          <Route
            path="/project"
            element={
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Projekte...</div>}>
                  <ProjectDashboard />
                </Suspense>
              </Layout></PrivateRoute>
            }
          />
          <Route
            path="/project/new"
            element={
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade...</div>}>
                  <ProjectNew />
                </Suspense>
              </Layout></PrivateRoute>
            }
          />
          <Route
            path="/project/boards/:id"
            element={
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Boards...</div>}>
                  <ProjectDashboard />
                </Suspense>
              </Layout></PrivateRoute>
            }
          />
          <Route
            path="/project/:id/*"
            element={
              <PrivateRoute><Layout>
                <Suspense fallback={<div className="p-6">Lade Projekt...</div>}>
                  <ProjectDetail />
                </Suspense>
              </Layout></PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
          </ChatProvider>
        </SmartcardProvider>
      </ToastProvider>
    </DarkModeProvider>
  );
}

